import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ShieldCheck, Truck, CreditCard, Headphones } from "lucide-react";
import { CONTACT, LOGO_URL } from "../mock";

const FOOTER_CATEGORIES = [
  { slug: "refrigerator-parts", name: "Refrigerator Parts" },
  { slug: "washer-parts", name: "Washer Parts" },
  { slug: "dryer-parts", name: "Dryer Parts" },
  { slug: "dishwasher-parts", name: "Dishwasher Parts" },
  { slug: "oven-range-parts", name: "Oven / Range Parts" },
  { slug: "microwave-parts", name: "Microwave Parts" },
  { slug: "bbq-parts-accessories", name: "BBQ Parts & Accessories" },
];

export default function Footer() {
  const badges = [
    { icon: ShieldCheck, title: "Genuine parts", desc: "Major brands covered" },
    { icon: Truck, title: "Fast shipping", desc: "Across Canada" },
    { icon: CreditCard, title: "Secure payment", desc: "Encrypted checkout" },
    { icon: Headphones, title: "Expert help", desc: "Model lookup support" },
  ];

  return (
    <footer className="mt-16">
      <div className="bg-secondary/60 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {badges.map((b) => (
            <div key={b.title} className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-rp-green/10 flex items-center justify-center shrink-0">
                <b.icon className="h-5 w-5 text-rp-green" />
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-900">{b.title}</p>
                <p className="text-xs text-muted-foreground">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-rp-nav text-white">
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img
                src={LOGO_URL}
                alt={CONTACT.brand}
                className="h-16 w-auto bg-white rounded-lg p-2 object-contain"
              />
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Appliance replacement parts for refrigerators, washers, dryers,
              dishwashers, ranges and more — powered by our live catalog.
            </p>
          </div>

          <div>
            <h4 className="font-display font-semibold mb-4 text-rp-green">Categories</h4>
            <ul className="space-y-2 text-sm text-white/80">
              {FOOTER_CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/boutique?menu=${c.slug}`} className="hover:text-white">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold mb-4 text-rp-green">Links</h4>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/boutique" className="hover:text-white">Shop</Link></li>
              <li><Link to="/model-lookup" className="hover:text-white">Model Lookup</Link></li>
              <li><Link to="/marques" className="hover:text-white">Brands</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/panier" className="hover:text-white">Cart</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold mb-4 text-rp-green">Contact</h4>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-rp-green shrink-0" />
                <span>{CONTACT.address}<br />{CONTACT.city}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-rp-green" />
                <a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-white">{CONTACT.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-rp-green" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-white break-all">{CONTACT.email}</a>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-rp-green" />
                <span>{CONTACT.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/60">
            <p>© {new Date().getFullYear()} {CONTACT.brand}. All rights reserved.</p>
            <p>Montréal, QC · Parts | Accessories | Lookup</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
